import { useEffect, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Target, Plus, Trash2, Percent, Clock } from "lucide-react";

export default function GoalDialog({ user, open, onOpenChange, onSaved }) {
  const [meta, setMeta] = useState(0);
  const [types, setTypes] = useState([]);
  const [cats, setCats] = useState({});
  const [comms, setComms] = useState({});
  const [commDefault, setCommDefault] = useState(0);
  // Metas & Comissões — modo da comissão: "percentage" (% sobre faturamento,
  // comportamento histórico) ou "fixed_hourly" (R$ por hora realizada). A
  // unidade vale tanto para a comissão padrão quanto para os overrides por
  // tipo de serviço — nunca há mistura dentro do mesmo analista.
  const [commMode, setCommMode] = useState("percentage");
  const hourly = commMode === "fixed_hourly";

  useEffect(() => {
    if (!open || !user) return;
    setMeta(user.meta_mensal || 0);
    setCats(user.metas_categoria || {});
    setComms(user.comissoes_categoria || {});
    setCommDefault(user.comissao_padrao || 0);
    setCommMode(user.commission_mode === "fixed_hourly" ? "fixed_hourly" : "percentage");
    api.get("/config/service-types").then(r => setTypes(r.data)).catch(() => {});
  }, [open, user]);

  // Trocar de unidade (% <-> R$/h) NUNCA reaproveita o número da unidade
  // antiga — evita "10%" virar "R$ 10/h" sem intenção. Zera a comissão
  // padrão e limpa os overrides por tipo de serviço.
  const changeMode = (m) => {
    if (m === commMode) return;
    setCommMode(m);
    setCommDefault(0);
    setComms({});
  };

  const addCat = (t) => setCats(c => ({ ...c, [t]: c[t] || 0 }));
  const removeCat = (t) => setCats(c => { const n = { ...c }; delete n[t]; return n; });
  const updCat = (t, v) => setCats(c => ({ ...c, [t]: parseFloat(v) || 0 }));

  const addComm = (t) => setComms(c => ({ ...c, [t]: c[t] || 0 }));
  const removeComm = (t) => setComms(c => { const n = { ...c }; delete n[t]; return n; });
  const updComm = (t, v) => setComms(c => ({ ...c, [t]: parseFloat(v) || 0 }));

  const save = async () => {
    try {
      const cleanCats = Object.fromEntries(Object.entries(cats).filter(([, v]) => v > 0));
      const cleanComms = Object.fromEntries(Object.entries(comms).filter(([, v]) => v > 0));
      await api.put(`/users/${user.id}`, {
        meta_mensal: parseFloat(meta) || 0,
        metas_categoria: cleanCats,
        commission_mode: commMode,
        comissoes_categoria: cleanComms,
        comissao_padrao: parseFloat(commDefault) || 0,
      });
      toast.success("Metas e comissões atualizadas");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return null;
  const available = types.map(t => t.nome).filter(n => !(n in cats));
  const commAvailable = types.map(t => t.nome).filter(n => !(n in comms));
  const catTotal = Object.values(cats).reduce((a, v) => a + (Number(v) || 0), 0);
  const commStep = hourly ? "1" : "0.5";
  const commPlaceholder = hourly ? "Ex.: 50" : "Ex.: 5";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Target size={18} className="text-primary" /> Metas & Comissões — {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto">
          <div>
            <Label>Meta mensal total (R$)</Label>
            <Input type="number" step="100" value={meta} onChange={e => setMeta(e.target.value)} data-testid="goal-input" />
            <div className="text-xs text-slate-400 mt-1">Atual: {money(meta || 0)}</div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Metas por tipo de serviço (opcional)</Label>
              <div className="text-xs text-slate-400">Soma: {money(catTotal)}</div>
            </div>
            <p className="text-xs text-slate-500 mb-3">Divida a meta entre categorias específicas. Aparecem como sub-progressos no dashboard.</p>
            <div className="space-y-2">
              {Object.entries(cats).map(([t, v]) => (
                <div key={t} className="flex items-center gap-2">
                  <div className="flex-1 text-sm font-medium">{t}</div>
                  <Input type="number" step="100" value={v} onChange={e => updCat(t, e.target.value)} className="w-40" data-testid={`goal-cat-${t}`} />
                  <Button size="sm" variant="ghost" onClick={() => removeCat(t)}><Trash2 size={14} className="text-red-600" /></Button>
                </div>
              ))}
              {available.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {available.map(t => (
                    <Button key={t} size="sm" variant="outline" onClick={() => addCat(t)} data-testid={`add-cat-${t}`}>
                      <Plus size={12} /> {t}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <Label className="text-sm">Tipo de comissão</Label>
            <div className="flex gap-2 mt-1.5 mb-3">
              <Button
                type="button" size="sm"
                variant={hourly ? "outline" : "default"}
                onClick={() => changeMode("percentage")}
                data-testid="comm-mode-percentage"
              >
                <Percent size={14} /> Percentual (%)
              </Button>
              <Button
                type="button" size="sm"
                variant={hourly ? "default" : "outline"}
                onClick={() => changeMode("fixed_hourly")}
                data-testid="comm-mode-fixed-hourly"
              >
                <Clock size={14} /> Valor por hora (R$)
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {hourly
                ? "Valor pago por hora efetivamente realizada nas fichas faturadas do analista (não usa o valor cobrado do cliente). A comissão por categoria sobrepõe a padrão."
                : "Percentual sobre o faturamento das fichas faturadas do analista. A comissão por categoria sobrepõe a padrão."}
            </p>
            <div className="mb-3">
              <Label className="text-xs">{hourly ? "Comissão padrão por hora (R$)" : "Comissão padrão (%)"}</Label>
              <Input type="number" step={commStep} min="0" value={commDefault} onChange={e => setCommDefault(e.target.value)} data-testid="comm-default" placeholder={commPlaceholder} />
              {hourly && <div className="text-xs text-slate-400 mt-1">Atual: {money(commDefault || 0)} / hora</div>}
            </div>
            <div className="space-y-2">
              {Object.entries(comms).map(([t, v]) => (
                <div key={t} className="flex items-center gap-2">
                  <div className="flex-1 text-sm font-medium">{t}</div>
                  {hourly && <span className="text-slate-400 text-sm">R$</span>}
                  <Input type="number" step={commStep} min="0" value={v} onChange={e => updComm(t, e.target.value)} className="w-28" placeholder={hourly ? "R$/h" : "%"} data-testid={`comm-cat-${t}`} />
                  <span className="text-slate-400">{hourly ? "/h" : "%"}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeComm(t)}><Trash2 size={14} className="text-red-600" /></Button>
                </div>
              ))}
              {commAvailable.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {commAvailable.map(t => (
                    <Button key={t} size="sm" variant="outline" onClick={() => addComm(t)} data-testid={`add-comm-${t}`}>
                      <Plus size={12} /> {t}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={save} data-testid="goal-save">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
